import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';

import {
  newCheckInAction,
  newNextAction,
  NEW_CHECKIN,
  NEW_NEXT,
} from '../../actions/navigation';
import { useConfig } from '../../config/ConfigContext';
import Header from '../../components/Header';
import Tooltip from '../../components/Tooltip';
import Input from '../../components/Form/Input';
import ApplicationId from '../../components/ApplicationId';
import Validator from '../../components/Validator';
import Progress from '../../components/Progress';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import schema from './continueSchema';
import { parseUrlParams, addLusaHeaderParam } from '../../utils/parseUrlParams';
import { numberUnmask } from '../../utils/unmasks';
import imgError from '../../assets/images/error.svg';

const Continue = (props) => {
  const config = useConfig();
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [uploadingPercentage, setUploadingPercentage] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  const { fetch, navigate, checkinData } = props;
  const match = useParams();
  const workflowType = get(match, 'workflowType') === 'luwa' ? 'dtm-luwa' : get(match, 'workflowType');

  useEffect(() => {
    const params = parseUrlParams(window.location.search);
    if (isEmpty(checkinData)) {
      props.newCheckInAction({
        workflowType,
        key: get(params, 'key'),
      });
    }
  }, []);

  useEffect(() => {
    if (
      !get(checkinData, 'data.data.returnUrl', get(checkinData, 'state.data.returnUrl'))
      && ((fetch.isDone[NEW_CHECKIN] && !fetch.isRunning[NEW_CHECKIN])
      || (fetch.isDone[NEW_NEXT] && !fetch.isRunning[NEW_NEXT]))
    ) {
      const params = parseUrlParams(window.location.search);
      let routeUrl = get(checkinData, 'data.url')?.replace(config.hostUrl, '');
      if (routeUrl && params.lusa_header && params.lusa_header === 'false') {
        routeUrl = addLusaHeaderParam(routeUrl);
      }
      navigate(routeUrl);
    }
  }, [checkinData]);

  const handleSubmitForm = (e) => {
    e.preventDefault();
    const { validator: { values, validate, errors } } = props;
    if (validate(schema).isValid) {
      const params = parseUrlParams(window.location.search);
      let formData;
      if (params.to) {
        formData = {
          SsnLast4Digits: numberUnmask(values.ssn),
          to: params.to,
        };
      } else {
        formData = {
          SsnLast4Digits: numberUnmask(values.ssn),
        };
      }
      setIsProgressVisible(true);
      props.newNextAction({
        entityType: 'application',
        workflowType,
        key: get(checkinData, 'key'),
        data: formData,
        onUploadProgress: (progressEvent) => {
          setUploadingPercentage(Math.floor((progressEvent.loaded * 100) / progressEvent.total));
        },
        fail: (err) => {
          console.log(err);
          if (err?.response?.data?.failure?.errors && err?.response?.data?.failure?.errors.length > 0) {
            setErrorMsg(err?.response?.data?.failure?.errors[0].message);
          }
          setIsPopupVisible(true);
          setIsProgressVisible(false);
        },
      });
    } else {
      console.log(errors);
      setIsPopupVisible(true);
      setIsProgressVisible(false);
    }
  };

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(event.target.name, event.target.value);
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false);
    setErrorMsg('');
  };

  const { validator: { values, errors } } = props;

  return (
    <div className="transition-item wrapper v-center">
      <Header />
      {get(props.checkinData, 'attributes.entityId') ? (
        <ApplicationId id={get(props.checkinData, 'attributes.entityId')} style={{ marginTop: 10 }} />
      ) : null}
      <div className="content" style={{ minHeight: 'calc(100vh - 200px)' }}>
        <h2>
          Welcome back,
          {' '}
          {get(checkinData, 'data.data.firstName')}
          !
        </h2>
        <h1>
          Congratulations!
          {' '}
          <br />
          You’re Pre-Approved
        </h1>
        <p>On the next screens you’ll be able to continue with your application.</p>
        <p className="mb-2">To proceed, please enter the last 4 of your SSN to verify your identity</p>
        <div className="container">
          <div className="row">
            <div className="col">
              <div className="form-group active">
                <Input
                  label="Last 4 Digits of Your SSN"
                  value={values.ssn || ''}
                  name="ssn"
                  tooltip="To protect your identity and for security purposes please enter the last 4 digits of your Social Security Number."
                  isMasked={[/\d/, /\d/, /\d/, /\d/]}
                  onChange={handleInputChange}
                  type="tel"
                  isRequired
                  hasError={errors.ssn}
                />
                <Tooltip>To protect your identity and for security purposes please enter the last 4 digits of your Social Security Number.</Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="footer">
        <div className="row">
          <div className="col">
            <Button
              className="btn btn-primary arrow"
              onClick={handleSubmitForm}
              title="Continue"
              isDisabled={values.ssn === undefined}
            />
          </div>
        </div>
      </div>
      {
        isPopupVisible && (
          <Modal>
            <div className="card-content error-modal security-error">
              <img src={imgError} alt="" />
              <p>{errorMsg || 'Please try again.'}</p>
              <div onClick={handleClosePopup} className="btn btn-red">OK</div>
            </div>
          </Modal>
        )
      }
      {
        isProgressVisible && <Progress percent={uploadingPercentage} />
      }
    </div>
  );
};

Continue.propTypes = {
  navigate: PropTypes.func.isRequired,
  validator: PropTypes.object.isRequired,
  checkinData: PropTypes.object.isRequired,
  fetch: PropTypes.object.isRequired,
  newNextAction: PropTypes.func.isRequired,
  newCheckInAction: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  fetch: state.fetch,
  checkinData: get(state, 'screen.checkinData'),
});

const mapDispatchToProps = ({
  newCheckInAction,
  newNextAction,
});

export default Validator(schema)(connect(mapStateToProps, mapDispatchToProps)(Continue));
